import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './ContactMessages.css';

const ContactMessages = () => {
  const [activeTab, setActiveTab] = useState('messages'); // 'messages' or 'newsletter'
  const [messages, setMessages] = useState([]);
  const [newsletterSubscriptions, setNewsletterSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filterUnread, setFilterUnread] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/contact/messages');
      let fetchedMessages = response.data;
      
      if (filterUnread) {
        fetchedMessages = fetchedMessages.filter(msg => !msg.isRead);
      }
      
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      setMessage('Error loading messages');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  }, [filterUnread]);

  const fetchNewsletterSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching newsletter subscriptions...');
      const response = await api.get('/api/newsletter/subscriptions');
      console.log('Newsletter subscriptions response:', response.data);
      
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setNewsletterSubscriptions(response.data);
        console.log(`Loaded ${response.data.length} newsletter subscriptions`);
      } else {
        console.error('Invalid response format:', response.data);
        setNewsletterSubscriptions([]);
        setMessage('Invalid data format received');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error fetching newsletter subscriptions:', error);
      console.error('Error details:', error.response?.data || error.message);
      setMessage(`Error loading newsletter subscriptions: ${error.response?.data?.error || error.message}`);
      setNewsletterSubscriptions([]);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages();
      // Refresh messages every 30 seconds to check for new ones
      const interval = setInterval(fetchMessages, 30000);
      return () => clearInterval(interval);
    } else if (activeTab === 'newsletter') {
      fetchNewsletterSubscriptions();
    }
  }, [fetchMessages, fetchNewsletterSubscriptions, activeTab]);

  const markAsRead = async (messageId) => {
    try {
      await api.put(`/api/contact/messages/${messageId}/read`);
      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, isRead: true } : msg
      ));
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage({ ...selectedMessage, isRead: true });
      }
      setMessage('Message marked as read');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error marking message as read:', error);
      setMessage('Error marking message as read');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }
    
    try {
      await api.delete(`/api/contact/messages/${messageId}`);
      setMessages(messages.filter(msg => msg.id !== messageId));
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage(null);
      }
      setMessage('Message deleted successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting message:', error);
      setMessage('Error deleting message');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unsubscribeEmail = async (email) => {
    if (!window.confirm(`Are you sure you want to unsubscribe ${email}?`)) {
      return;
    }
    
    try {
      await api.post('/api/newsletter/unsubscribe', { email });
      setMessage('Email unsubscribed successfully');
      setTimeout(() => setMessage(''), 3000);
      fetchNewsletterSubscriptions(); // Refresh list
    } catch (error) {
      console.error('Error unsubscribing email:', error);
      setMessage('Error unsubscribing email');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const unreadCount = messages.filter(msg => !msg.isRead).length;

  if (loading) {
    return (
      <div className="contact-messages">
        <div className="loading">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="contact-messages">
      <div className="contact-messages-header">
        <h2>📧 Contact & Newsletter</h2>
        <div className="header-actions">
          {activeTab === 'messages' && (
            <>
              <button
                className={`filter-btn ${filterUnread ? 'active' : ''}`}
                onClick={() => setFilterUnread(!filterUnread)}
              >
                {filterUnread ? 'Show All' : `Show Unread (${unreadCount})`}
              </button>
              <button
                className="refresh-btn"
                onClick={fetchMessages}
              >
                🔄 Refresh
              </button>
            </>
          )}
          {activeTab === 'newsletter' && (
            <button
              className="refresh-btn"
              onClick={fetchNewsletterSubscriptions}
            >
              🔄 Refresh
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="contact-tabs">
        <button
          className={`contact-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          📨 Contact Messages ({messages.length})
        </button>
        <button
          className={`contact-tab ${activeTab === 'newsletter' ? 'active' : ''}`}
          onClick={() => setActiveTab('newsletter')}
        >
          📧 Newsletter Subscriptions ({newsletterSubscriptions.length})
        </button>
      </div>

      {message && (
        <div className="message">
          {message}
        </div>
      )}

      {/* Contact Messages Tab */}
      {activeTab === 'messages' && (
        <div className="messages-container">
        <div className="messages-list">
          <div className="messages-stats">
            <span>Total: {messages.length}</span>
            <span className="unread-badge">Unread: {unreadCount}</span>
          </div>
          
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No contact messages {filterUnread ? 'unread' : 'available'}</p>
            </div>
          ) : (
            <div className="messages-grid">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-card ${!msg.isRead ? 'unread' : ''}`}
                  onClick={() => {
                    setSelectedMessage(msg);
                    if (!msg.isRead) {
                      markAsRead(msg.id);
                    }
                  }}
                >
                  <div className="message-card-header">
                    <div className="message-sender">
                      <strong>{msg.name}</strong>
                      {!msg.isRead && <span className="unread-indicator">●</span>}
                    </div>
                    <div className="message-date">{formatDate(msg.createdAt)}</div>
                  </div>
                  <div className="message-email">{msg.email}</div>
                  <div className="message-preview">
                    {msg.message.length > 100 
                      ? `${msg.message.substring(0, 100)}...` 
                      : msg.message}
                  </div>
                  <div className="message-actions">
                    <button
                      className="action-btn read-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(msg.id);
                      }}
                      disabled={msg.isRead}
                    >
                      {msg.isRead ? '✓ Read' : 'Mark as Read'}
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMessage(msg.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedMessage && (
          <div className="message-detail">
            <div className="message-detail-header">
              <h3>Message Details</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedMessage(null)}
              >
                ×
              </button>
            </div>
            <div className="message-detail-content">
              <div className="detail-row">
                <label>From:</label>
                <div>
                  <strong>{selectedMessage.name}</strong>
                  <br />
                  <span className="email">{selectedMessage.email}</span>
                </div>
              </div>
              <div className="detail-row">
                <label>Date:</label>
                <div>{formatDate(selectedMessage.createdAt)}</div>
              </div>
              <div className="detail-row">
                <label>Status:</label>
                <div>
                  {selectedMessage.isRead ? (
                    <span className="status-read">✓ Read</span>
                  ) : (
                    <span className="status-unread">● Unread</span>
                  )}
                </div>
              </div>
              <div className="detail-row full-width">
                <label>Message:</label>
                <div className="message-text">{selectedMessage.message}</div>
              </div>
              <div className="message-detail-actions">
                {!selectedMessage.isRead && (
                  <button
                    className="action-btn read-btn"
                    onClick={() => markAsRead(selectedMessage.id)}
                  >
                    Mark as Read
                  </button>
                )}
                <button
                  className="action-btn delete-btn"
                  onClick={() => deleteMessage(selectedMessage.id)}
                >
                  Delete Message
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Newsletter Subscriptions Tab */}
      {activeTab === 'newsletter' && (
        <div className="newsletter-container">
          <div className="newsletter-stats">
            <span>Total Subscribers: {newsletterSubscriptions.length}</span>
          </div>
          
          {newsletterSubscriptions.length === 0 ? (
            <div className="no-messages">
              <p>No newsletter subscriptions yet</p>
            </div>
          ) : (
            <div className="subscriptions-grid">
              {newsletterSubscriptions.map((subscription) => (
                <div key={subscription.id} className="subscription-card">
                  <div className="subscription-header">
                    <div className="subscription-email">
                      <strong>{subscription.email}</strong>
                    </div>
                    <div className="subscription-date">{formatDate(subscription.subscribedAt)}</div>
                  </div>
                  <div className="subscription-status">
                    <span className={`status-badge ${subscription.isActive ? 'active' : 'inactive'}`}>
                      {subscription.isActive ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </div>
                  {subscription.isActive && (
                    <div className="subscription-actions">
                      <button
                        className="action-btn unsubscribe-btn"
                        onClick={() => unsubscribeEmail(subscription.email)}
                      >
                        Unsubscribe
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactMessages;

